import { useEffect } from 'react'
import { useSelector } from 'react-redux'
import {
  Stack,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  Chip,
} from '@mui/material'
import EditIcon from '@mui/icons-material/Edit'
import SaveIcon from '@mui/icons-material/Save'
import CloseIcon from '@mui/icons-material/Close'
import MUIInput from '../../common/field/MUIInput'
import MultiLangInput from '../../common/field/MultiLangInput'
import { useSurveyForm } from '../../../hooks/useSurveyForm'
import { LANGS_WITHOUT_TW } from '../../../Constants/lang'

const LANGS = LANGS_WITHOUT_TW

/** Ensure a multi-lang object has all required locales (fill missing with '') */
const fillLocales = (obj) => {
  if (!obj || typeof obj !== 'object') return { ...Object.fromEntries(LANGS.map(l => [l, ''])) }
  const result = {}
  LANGS.forEach(l => { result[l] = obj[l] || '' })
  return result
}

const defaultData = {
  title: { EN: '', ZH_CN: '', ZH_HK: '' },
  description: { EN: '', ZH_CN: '', ZH_HK: '' },
  remarks: '',
}

/**
 * Base Survey Dialog Component
 * Reusable dialog for creating and editing survey metadata
 * Provides form handling and validation
 */
export default function BaseSurveyDialog({
  isEdit = false,
  open = false,
  toggleOpen,
  handleSubmit,
  title = 'Create Survey',
  initialData = defaultData,
}) {
  const savedSurveyFlow = useSelector((state) => state.surveyFlow.ui.surveyFlow)
  const {
    data,
    error,
    handleChange,
    handleMultiLangChange,
    validate,
    setData,
  } = useSurveyForm(initialData, ['title'])

  const handleClose = () => toggleOpen(false)

  const onSubmit = () => {
    if (validate()) {
      handleSubmit(data)
      handleClose()
    }
  }

  useEffect(() => {
    if (isEdit && savedSurveyFlow) {
      // Normalize sparse API data: fill missing locales with ''
      setData({
        ...savedSurveyFlow,
        title: fillLocales(savedSurveyFlow.title),
        description: fillLocales(savedSurveyFlow.description),
      })
    }
  }, [isEdit, savedSurveyFlow, setData])

  const sectionSx = { color: 'text.secondary', fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, mb: 0.5 }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth='md'
      fullWidth
      PaperProps={{ sx: { borderRadius: '12px', overflow: 'hidden' } }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5, pb: 1 }}>
        {isEdit ? <EditIcon fontSize="small" color="action" /> : <SaveIcon fontSize="small" color="action" />}
        <span>{title}</span>
        {isEdit && savedSurveyFlow?.id && (
          <Chip label={savedSurveyFlow.id?.slice(0, 8)} size="small" variant="outlined" sx={{ ml: 'auto', fontFamily: 'monospace', fontSize: 11 }} />
        )}
      </DialogTitle>

      <DialogContent dividers sx={{ py: 2.5 }}>
        <Stack spacing={3}>
          {/* Title Section */}
          <div>
            <Typography sx={sectionSx}>Title <Chip label="Required" size="small" sx={{ ml: 0.5, height: 18, fontSize: 10, bgcolor: '#fef2f2', color: '#dc2626' }} /></Typography>
            <MultiLangInput
              langObj={data.title}
              objKey='title'
              onChangeMultiLangInput={handleMultiLangChange}
              error={error.title}
              required
            />
          </div>

          {/* Description Section */}
          <div>
            <Typography sx={sectionSx}>Description</Typography>
            <MultiLangInput
              langObj={data.description}
              objKey='description'
              onChangeMultiLangInput={handleMultiLangChange}
              error={error.description}
              multiline
              rows={3}
            />
          </div>

          {/* Remarks Section */}
          <div>
            <Typography sx={sectionSx}>Remarks</Typography>
            <MUIInput
              value={data.remarks}
              onChange={(e) => handleChange('remarks', e.target.value)}
              error={!!error.remarks}
              helperText={error.remarks}
              fullWidth
              multiline
              rows={2}
            />
          </div>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 1.5, gap: 1 }}>
        <Button onClick={handleClose} startIcon={<CloseIcon />} color="inherit">{'Cancel'}</Button>
        <Button onClick={onSubmit} variant='contained' startIcon={<SaveIcon />}>
          {isEdit ? 'Update' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
